/**
 * useAppProviders — the app-level provider / substrate wiring.
 *
 * One cohesive concern: everything App.vue stands up *once* at the root so
 * the rest of the tree can read it. That spans three families:
 *
 *   - Substrate Pinia singletons — instantiated here so the stores exist
 *     before any child reads them (variant, journey, sessionExpiry, api,
 *     speedtest).
 *   - The document `.dark`-class cascade — `useGlobalDark()` installs the
 *     class glass-ui primitives (Toaster, Dialog, Aurora defaults) read
 *     directly from `documentElement`.
 *   - The per-app stateful provider instances — `useIPInfo`,
 *     `useConnectionType`, `useGeolocation` — whose constructors belong in
 *     the root setup, plus their `provide()` of the canonical injection
 *     keys.
 *
 * `provide()` must run synchronously in setup scope; this composable is
 * called directly from App.vue's `<script setup>`, so the provides land in
 * the root component's provide registry exactly as they did inline.
 *
 * `connectionType` is returned because the dock-orchestration concern reads
 * `connectionType.isCellular` for its cellular preflight — App.vue threads
 * the returned handle into `useDockOrchestration`.
 */

import { provide } from "vue";
import { useGlobalDark } from "@mkbabb/glass-ui/dark";

import { useIPInfo } from "@src/composables/useIPInfo.ts";
import { useGeolocation } from "@src/composables/useGeolocation.ts";
import { useConnectionType } from "@src/composables/useConnectionType.ts";
import {
    IPInfoKey,
    GeolocationKey,
    ConnectionTypeKey,
} from "@src/composables/keys.ts";

import { useVariantStore } from "@src/stores/useVariantStore.ts";
import { useJourneyStore } from "@src/stores/useJourneyStore.ts";
import { useApiStore } from "@src/stores/useApiStore.ts";
import { useSpeedtestStore } from "@features/speedtest";
import { useSessionExpiryStore } from "@src/stores/useSessionExpiryStore.ts";

export function useAppProviders() {
    // ── Substrate Pinia singletons ─────────────────────────────────────
    useVariantStore();
    useJourneyStore();
    useSessionExpiryStore();
    useApiStore();
    useSpeedtestStore();

    // ── Dark mode + IP info + connection + geolocation + servers ─────────

    // `useGlobalDark()` installs the document `.dark`-class cascade that
    // glass-ui primitives (Toaster, Dialog, Aurora's defaults) read directly
    // — the prior `isDarkMode` ref / `isDark` prop bridge into `ToastProvider`
    // retired at AI.W5-ζ (the Toaster reads `.dark` from `documentElement`).
    useGlobalDark();

    const ipInfoProvider = useIPInfo();
    provide(IPInfoKey, ipInfoProvider);

    const connectionType = useConnectionType();
    provide(ConnectionTypeKey, connectionType);

    const geolocation = useGeolocation();
    provide(GeolocationKey, geolocation);

    return {
        connectionType,
    };
}
